import { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { styled } from '@mui/material/styles';
import TableContainer from '@mui/material/TableContainer';
import { Typography } from '@components/Typography';
import DeleteIcon from '@mui/icons-material/DeleteRounded';
import ActionIconButton from '@components/ActionIconButton';
import EditIcon from '@mui/icons-material/EditRounded';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Table, getPaginationLabels } from '@components/Table';
import { ModalConfirm } from '@components/Modal';
import { MEDIA_TYPE } from '@constants/file';
import { CAMPAIGN_STATUS } from '@constants/social-posts';
import { formatDateOrEmpty, isMaxDate } from '@helpers/dates';
import { useTranslation } from '@hooks';
import { alertActions, socialPostsActions } from '@actions';
import { socialPostsService } from '@services';
import { handleApiError } from '@helpers/api';
import PlatformIcon from './PlatformIcon';
import CampaignStatusChip from './CampaignStatusChip';
import PostOutcomeSummary from './PostOutcomeSummary';
import { EntityChip, ChipsCell } from '@components/EntityChips';
import { buildCollapsibleRows } from './CollapsibleRowBody';
import { COLUMN_WIDTHS, TABLE_MIN_WIDTH } from './columns';

const POST_ACTION_CONFIRM = {
    cancel: {
        title: 'SocialPosts.cancelPostTitle',
        description: 'SocialPosts.cancelPostDescription',
        primary: 'SocialPosts.cancelPost',
        primaryType: 'contained',
        success: 'alertMessages.cancelSuccess',
        failure: 'alertMessages.cancelFail',
    },
    delete: {
        title: 'SocialPosts.deletePostTitle',
        description: 'SocialPosts.deletePostDescription',
        primary: 'buttons.delete',
        primaryType: 'danger',
        success: 'alertMessages.deleteSuccess',
        failure: 'alertMessages.deleteFail',
    },
};

const StyledTable = styled(Table)({
    minWidth: TABLE_MIN_WIDTH,
    tableLayout: 'fixed',
});

// Header hidden so the first body row drives the fixed column widths, keeping per-post rows
// aligned with the campaign table above.
const StyledCollapsibleTable = styled(Table)({
    minWidth: TABLE_MIN_WIDTH,
    tableLayout: 'fixed',
    '& thead': { display: 'none' },
});

const TitleCell = styled('div')({
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    width: '100%',
    minWidth: 160,
});

const MediaThumb = styled('div')({
    width: 40,
    height: 40,
    flexShrink: 0,
    borderRadius: 6,
    overflow: 'hidden',
    backgroundColor: '#000',
    '& img, & video': {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        display: 'block',
    },
});

// Prefer the campaign's base media, falling back to the first override that carries its own.
const getPreviewMedia = campaign => {
    const fromBase = Array.isArray(campaign?.media) ? campaign.media.find(m => m?.link) : null;
    if (fromBase) return fromBase;
    const overrides = Array.isArray(campaign?.overrides) ? campaign.overrides : [];
    for (const override of overrides) {
        const fromOverride = Array.isArray(override?.media) ? override.media.find(m => m?.link) : null;
        if (fromOverride) return fromOverride;
    }
    return null;
};

const getAssignedLocationNames = (companies, assignedIds = []) =>
    companies.filter(c => assignedIds.includes(c.companyId)).map(c => c.internalName);

const renderTitleCell = campaign => {
    const preview = getPreviewMedia(campaign);
    return (
        <TitleCell>
            {preview && (
                <MediaThumb>
                    {preview.type === MEDIA_TYPE.VIDEO ? (
                        // #t=0.1 makes the browser paint the first frame as a static poster.
                        <video src={`${preview.link}#t=0.1`} muted playsInline preload="metadata" />
                    ) : (
                        <img src={preview.link} alt="" />
                    )}
                </MediaThumb>
            )}
            <Typography
                variant="subtitle2"
                align="left"
                sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            >
                {campaign.title}
            </Typography>
        </TitleCell>
    );
};

const renderChipsCell = (campaign, companies, allCompanies, t) => {
    if (allCompanies) {
        return (
            <ChipsCell>
                <EntityChip label={t('common.all')} />
            </ChipsCell>
        );
    }
    const names = getAssignedLocationNames(companies, campaign.companies);
    if (!names.length) {
        return (
            <ChipsCell>
                <Typography variant="body2">--</Typography>
            </ChipsCell>
        );
    }
    return (
        <ChipsCell>
            {names.map((name, index) => (
                <EntityChip key={`${name}-${index}`} label={name} />
            ))}
        </ChipsCell>
    );
};

const getTableHeader = t => [
    // Lines the header up with the expand-caret cell the collapsible table injects.
    { key: 'expand', width: COLUMN_WIDTHS.expand, component: <></> },
    {
        key: 'title',
        width: COLUMN_WIDTHS.title,
        component: (
            <Typography align="left" variant="subtitle2">
                {t('common.title')}
            </Typography>
        ),
    },
    {
        key: 'locations',
        width: COLUMN_WIDTHS.locations,
        component: (
            <Typography align="left" variant="subtitle2">
                {t('common.locations')}
            </Typography>
        ),
    },
    {
        key: 'platforms',
        width: COLUMN_WIDTHS.platforms,
        component: (
            <Typography align="left" variant="subtitle2">
                {t('common.platforms')}
            </Typography>
        ),
    },
    {
        key: 'scheduledTime',
        width: COLUMN_WIDTHS.scheduledTime,
        component: (
            <Typography align="left" variant="subtitle2">
                {t('common.scheduledTime')}
            </Typography>
        ),
    },
    {
        key: 'status',
        width: COLUMN_WIDTHS.status,
        component: (
            <Typography align="left" variant="subtitle2">
                {t('common.status')}
            </Typography>
        ),
    },
    { key: 'actions', width: COLUMN_WIDTHS.actions, component: <Typography align="left" variant="subtitle2" /> },
];

const getTableBody = (data, language, companies, onClick, t) => {
    return data.map(campaign => {
        const allCompanies = companies.length === campaign.companies.length;
        // Edit/Delete use server-computed flags (write access to ALL campaign locations). A
        // completed campaign is immutable regardless of permission; individual posts can still be
        // deleted from the expanded row where supported.
        const isCompleted = campaign.status === CAMPAIGN_STATUS.COMPLETED;
        const canEdit = !!campaign.canEdit && !isCompleted;
        const canDelete = !!campaign.canDelete && !isCompleted;
        return [
            { component: renderTitleCell(campaign) },
            { component: renderChipsCell(campaign, companies, allCompanies, t) },
            {
                component: (
                    <Typography
                        variant="body2"
                        align="left"
                        sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}
                    >
                        {campaign.platforms.map(platform => (
                            <span key={platform} style={{ width: 18, height: 18 }}>
                                <PlatformIcon platform={platform} size={18} />
                            </span>
                        ))}
                    </Typography>
                ),
            },
            {
                component: (
                    <Typography variant="body2" align="left" sx={{ whiteSpace: 'nowrap' }}>
                        {isMaxDate(campaign.scheduledTime)
                            ? ''
                            : formatDateOrEmpty(campaign.scheduledTime, 'dd MMM yyyy, HH:mm', language)}
                    </Typography>
                ),
            },
            {
                component: (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 6 }}>
                        <CampaignStatusChip status={campaign.status} t={t} />
                        <PostOutcomeSummary posts={campaign.posts} t={t} />
                    </div>
                ),
            },
            {
                component: (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                        <ActionIconButton title={t('common.view')} onClick={() => onClick(campaign, 'view')}>
                            <VisibilityIcon viewBox="0 0 22 22" />
                        </ActionIconButton>
                        {canEdit && (
                            <ActionIconButton title={t('common.edit')} onClick={() => onClick(campaign, 'edit')}>
                                <EditIcon viewBox="0 0 22 22" />
                            </ActionIconButton>
                        )}
                        {canDelete && (
                            <ActionIconButton title={t('common.delete')} onClick={() => onClick(campaign, 'delete')}>
                                <DeleteIcon viewBox="0 0 22 22" />
                            </ActionIconButton>
                        )}
                    </div>
                ),
            },
        ];
    });
};

// Module-scoped so its identity is stable and the parent Table doesn't re-mount it each render.
const CollapsibleRow = ({ data, index, companies, onRetry, onCancelDelete, t }) => {
    const campaign = data?.[index];
    // Per-post actions follow write permission (canModifyPosts) only, so they stay available on a
    // Completed campaign where campaign.canEdit is intentionally false.
    const canModify = !!campaign?.canModifyPosts;
    const rows = buildCollapsibleRows(campaign?.posts || [], companies, { onRetry, onCancelDelete }, t, canModify);
    if (!rows.length) return null;
    return <StyledCollapsibleTable rows={rows} />;
};

const SocialPostsTable = ({
    rowsPerPageRequests,
    pageRequests,
    onPageChange,
    onRowsPerPageChange,
    onClickSocialPost,
    refetchAvailablePlatforms,
    currentQuery,
}) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { data, loading, totalCount, pages } = useSelector(state => state.socialPosts);
    const language = useSelector(state => state.auth.user?.language);
    const companies = useSelector(state => state.account?.account?.companies || []);

    const onRetry = useCallback(
        async post => {
            try {
                const updated = await socialPostsService.retryPost(post.id);
                dispatch(alertActions.success(t('alertMessages.retrySuccess')));
                // Patching the returned row keeps the campaign expanded; a refetch remounts the
                // table body and collapses it.
                if (updated?.id) {
                    dispatch(socialPostsActions.updatePost(updated));
                } else {
                    dispatch(socialPostsActions.get(pageRequests, rowsPerPageRequests, currentQuery));
                }
            } catch (error) {
                handleApiError(error, 'alertMessages.retryFail');
            }
        },
        [dispatch, t, pageRequests, rowsPerPageRequests, currentQuery],
    );

    const [confirm, setConfirm] = useState({ open: false, post: null, action: 'delete' });
    const closeConfirm = useCallback(() => setConfirm(prev => ({ ...prev, open: false })), []);

    const requestCancelDelete = useCallback((post, action) => {
        setConfirm({ open: true, post, action });
    }, []);

    const onConfirmCancelDelete = useCallback(async () => {
        const { post, action } = confirm;
        if (!post) return;
        try {
            await socialPostsService.cancelOrDeletePost(post.id, action);
            dispatch(alertActions.success(t(POST_ACTION_CONFIRM[action].success)));
            refetchAvailablePlatforms?.();
        } catch (error) {
            handleApiError(error, POST_ACTION_CONFIRM[action].failure);
        } finally {
            // Always refresh: on a conflict (status changed since the dialog opened) the row
            // must reflect the new status so the user sees the correct action next time.
            dispatch(socialPostsActions.get(pageRequests, rowsPerPageRequests, currentQuery));
            closeConfirm();
        }
    }, [
        confirm,
        dispatch,
        t,
        pageRequests,
        rowsPerPageRequests,
        currentQuery,
        refetchAvailablePlatforms,
        closeConfirm,
    ]);

    const renderCollapsibleRow = useCallback(
        rowProps => (
            <CollapsibleRow
                {...rowProps}
                companies={companies}
                onRetry={onRetry}
                onCancelDelete={requestCancelDelete}
                t={t}
            />
        ),
        [companies, onRetry, requestCancelDelete, t],
    );

    return (
        <TableContainer>
            <StyledTable
                withHover
                columnNames={getTableHeader(t)}
                rows={getTableBody(data, language, companies, onClickSocialPost, t)}
                data={data}
                isDataLoading={loading}
                isCollapsible
                CollapsibleRow={renderCollapsibleRow}
                withPagination
                paginationProps={{
                    colSpan: 7,
                    count: totalCount,
                    rowsPerPage: rowsPerPageRequests,
                    page: pageRequests,
                    onPageChange,
                    onRowsPerPageChange,
                    ...getPaginationLabels(t),
                    pages,
                }}
            />
            {confirm.open && (
                <ModalConfirm
                    isOpen={confirm.open}
                    handleClose={closeConfirm}
                    title={t(POST_ACTION_CONFIRM[confirm.action].title)}
                    description={t(POST_ACTION_CONFIRM[confirm.action].description)}
                    onPrimaryAction={onConfirmCancelDelete}
                    primaryActionText={t(POST_ACTION_CONFIRM[confirm.action].primary)}
                    primaryActionType={POST_ACTION_CONFIRM[confirm.action].primaryType}
                />
            )}
        </TableContainer>
    );
};

export default SocialPostsTable;
